import React, { PureComponent } from 'react';

import { flatten } from 'lodash-es';
import { FormattedMessage } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import Fact, { uncurlyUuid } from '@atlassian/bitkit-analytics';

import { CodeSearchResult } from 'src/types/search';
import {
  publishFact,
  publishUiEvent,
  UiEvent,
} from 'src/utils/analytics/publish';

import {
  SearchResultsCodeExpandClickedFact,
  SearchResultsCodeCollapseClickedFact,
} from '../../facts';

import Hunk from './hunk';
import messages from './search-content-hit.i18n';
import SearchFile from './search-file';
import { srcHref, filename } from './utils';

type ResultProps = {
  title: JSX.Element;
  pageNum: number;
  result: CodeSearchResult;
  resultNum: number;
  accountUuid: string;
};

type ResultState = {
  expanded: boolean;
};

const MAX_LINES_WHEN_COLLAPSED = 9;

type FooterProps = {
  children?: any;
  fact: Fact<any>;
  Icon: JSX.Element;
  onClick: () => void;
  uiEvent: UiEvent;
};

const Footer = ({ children, Icon, fact, uiEvent, ...props }: FooterProps) => (
  <CustomThemeButton
    appearance="link"
    iconBefore={Icon}
    {...props}
    onClick={() => {
      publishFact(fact);
      publishUiEvent(uiEvent);
      props.onClick();
    }}
  >
    {children}
  </CustomThemeButton>
);

export default class SearchResult extends PureComponent<
  ResultProps,
  ResultState
> {
  toggleExpand: () => void;

  constructor(props: ResultProps) {
    super(props);
    this.toggleExpand = () =>
      this.setState(oldState => ({ expanded: !oldState.expanded }));
  }

  state = {
    expanded: false,
  };

  getVisibleHunkLines() {
    const {
      result: { content_matches: hunks },
    } = this.props;
    if (this.state.expanded) {
      return (hunks || []).map(hunk => hunk.lines);
    }
    let linesShown = 0;
    return (hunks || [])
      .map(hunk => {
        const linesToShow = Math.min(
          Math.max(MAX_LINES_WHEN_COLLAPSED - linesShown, 0),
          hunk.lines.length
        );
        linesShown += linesToShow;
        return hunk.lines.slice(0, linesToShow);
      })
      .filter(lines => lines.length !== 0);
  }

  render() {
    const { pageNum, result, resultNum, accountUuid } = this.props;
    const {
      file: { path },
    } = result;

    const hunkLines = this.getVisibleHunkLines();
    const visibleMatchesCount = flatten(hunkLines).reduce(
      (currCount, line) =>
        currCount + line.segments.filter(s => !!s.match).length,
      0
    );
    const totalLineCount = (result.content_matches || []).reduce(
      (currCount, hunk) => currCount + hunk.lines.length,
      0
    );

    const footer = this.state.expanded ? (
      <Footer
        fact={new SearchResultsCodeCollapseClickedFact()}
        uiEvent={{
          action: 'clicked',
          actionSubject: 'button',
          actionSubjectId: 'codeCollapseButton',
          source: 'search',
          containerType: 'account',
          containerId: uncurlyUuid(accountUuid),
        }}
        onClick={this.toggleExpand}
        Icon={<ChevronUpIcon label="" />}
      >
        <FormattedMessage {...messages.collapse} />
      </Footer>
    ) : (
      <Footer
        fact={new SearchResultsCodeExpandClickedFact()}
        uiEvent={{
          action: 'clicked',
          actionSubject: 'button',
          actionSubjectId: 'codeExpandButton',
          source: 'search',
          containerType: 'account',
          containerId: uncurlyUuid(accountUuid),
        }}
        onClick={this.toggleExpand}
        Icon={<ChevronDownIcon label="Expand code" />}
      >
        {visibleMatchesCount < result.content_match_count ? (
          <FormattedMessage
            {...messages.expandMatches}
            values={{
              count: <strong>{result.content_match_count}</strong>,
            }}
          />
        ) : (
          <FormattedMessage {...messages.expandContext} />
        )}
      </Footer>
    );

    return (
      <SearchFile
        title={this.props.title}
        key={path}
        footer={totalLineCount > MAX_LINES_WHEN_COLLAPSED && footer}
      >
        {hunkLines.map((lines, idx) => (
          <Hunk
            filename={filename(path)}
            key={(lines.length && lines[0].line) || idx}
            lines={lines}
            resultNum={resultNum}
            pageNum={pageNum}
            srcHref={
              result.file.commit.hash
                ? srcHref(result.file.commit, path)
                : undefined
            }
            accountUuid={accountUuid}
          />
        ))}
      </SearchFile>
    );
  }
}
